/**
 * Data layer for `auto-alert-publisher-manager-email`.
 * Thin typed fetch wrappers over /api/v1/auto-alert-publisher-manager-email.
 * Raw `fetch()` is confined to this file per the dash contract.
 *
 * Backend shape (see apps/api/src/routes/auto-alert-publisher-manager-email.ts):
 *  POST  /                 ingest alert event (idempotent on event_id)
 *  GET   /?status=...      list alerts for tenant (optional status filter)
 *  POST  /:id/mark-sent    mark an alert as delivered
 */

export type AlertStatus = "pending" | "sent" | "failed";

export interface AutoAlertPublisherManagerEmailRow {
	id: string;
	tenant_id: string;
	event_id: string;
	manager_email: string;
	manager_id: string | null;
	affiliate_id: string | null;
	event_type: string;
	subject: string;
	body: string;
	context_json: string | null;
	status: AlertStatus;
	sent_at: number | null;
	created_at: number;
}

export interface ListAutoAlertPublisherManagerEmailResponse {
	items: AutoAlertPublisherManagerEmailRow[];
}

export interface CreateAutoAlertPublisherManagerEmailBody {
	event_id: string;
	manager_email: string;
	event_type: string;
	subject: string;
	body: string;
	manager_id?: string;
	affiliate_id?: string;
	context?: unknown;
}

export interface CreateAutoAlertPublisherManagerEmailResponse {
	ok: true;
	id: string;
	deduped: boolean;
}

export interface MarkSentResponse {
	ok: true;
}

const BASE = "/api/v1/auto-alert-publisher-manager-email";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(
	status?: AlertStatus,
): Promise<ListAutoAlertPublisherManagerEmailResponse> {
	const url = status ? `${BASE}?status=${encodeURIComponent(status)}` : BASE;
	const res = await fetch(url, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListAutoAlertPublisherManagerEmailResponse>(res);
}

export async function create(
	body: CreateAutoAlertPublisherManagerEmailBody,
): Promise<CreateAutoAlertPublisherManagerEmailResponse> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<CreateAutoAlertPublisherManagerEmailResponse>(res);
}

export async function markSent(id: string): Promise<MarkSentResponse> {
	const res = await fetch(
		`${BASE}/${encodeURIComponent(id)}/mark-sent`,
		{
			method: "POST",
			credentials: "include",
			headers: { accept: "application/json" },
		},
	);
	return parseOrThrow<MarkSentResponse>(res);
}

export const autoAlertPublisherManagerEmailQueryKeys = {
	all: ["auto-alert-publisher-manager-email"] as const,
	list: (status?: AlertStatus) =>
		status
			? ([...["auto-alert-publisher-manager-email"], "list", status] as const)
			: ([...["auto-alert-publisher-manager-email"], "list"] as const),
	detail: (id: string) =>
		[...["auto-alert-publisher-manager-email"], id] as const,
};
